"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResolvedFileCard = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const Paper_1 = require("@jsonjoy.com/ui/lib/4-card/Paper");
const FileIcon_1 = require("@jsonjoy.com/ui/lib/1-inline/FileIcon");
const FileListItem_1 = require("@jsonjoy.com/ui/lib/3-list-item/FileListItem");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const flexible_input_1 = require("flexible-input");
const MediaPreview_1 = require("./MediaPreview");
const BasicButtonMore_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton/BasicButtonMore");
const Button_1 = require("@jsonjoy.com/ui/lib/2-inline-block/Button");
const Popup_1 = require("@jsonjoy.com/ui/lib/4-card/Popup");
const FileOptionsPopup_1 = require("./FileOptionsPopup");
const Iconista_1 = require("@jsonjoy.com/ui/lib/icons/Iconista");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const use_t_1 = require("use-t");
const state_1 = require("./state");
const VoidSelectionOverlay_1 = require("../VoidSelectionOverlay");
const context_2 = require("../../../context");
const captionClass = (0, nano_theme_1.rule)({
    textAlign: 'center',
    fz: '13px',
    lh: 1.4,
    mt: '6px',
});
const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 0)
        return '';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
const getExtensionLabel = (thing) => {
    if (thing.name) {
        const dot = thing.name.lastIndexOf('.');
        if (dot >= 0 && dot < thing.name.length - 1)
            return thing.name.slice(dot + 1).toLowerCase();
    }
    if (thing.mimeType) {
        const slash = thing.mimeType.indexOf('/');
        if (slash >= 0)
            return thing.mimeType.slice(slash + 1).toLowerCase();
    }
    return 'file';
};
const getMediaKind = (mime) => {
    if (/^image\//i.test(mime))
        return 'image';
    if (/^video\//i.test(mime))
        return 'video';
    if (/^audio\//i.test(mime))
        return 'audio';
    return null;
};
const stopMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
const bindVoidInputKeyDown = (el, editor, onCancel) => {
    if (!el)
        return () => { };
    const handler = (evt) => {
        const e = evt;
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel?.();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
};
const ResolvedFileCard = ({ thing, selected, element }) => {
    const [t] = (0, use_t_1.useT)();
    const mutxt = (0, context_2.useMuTxt)();
    const styles = (0, context_1.useStyles)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const mediaKind = getMediaKind(thing.mimeType || '');
    const [nameValue, setNameValue] = React.useState(thing.name ?? '');
    const focused = React.useRef(false);
    React.useEffect(() => {
        if (!focused.current)
            setNameValue(thing.name ?? '');
    }, [thing.name]);
    const commitName = () => {
        focused.current = false;
        const obj = mutxt.things.obj.obj(thing['@id'], true);
        obj?.mergeKeys({ name: nameValue.trim() || undefined });
    };
    const [nameInputEl, setNameInputEl] = React.useState(null);
    React.useEffect(() => {
        if (readOnly)
            return;
        return bindVoidInputKeyDown(nameInputEl, mutxt.editor, () => {
            setNameValue(thing.name ?? '');
            focused.current = false;
        });
    }, [nameInputEl, readOnly, thing.name, mutxt.editor]);
    const [captionValue, setCaptionValue] = React.useState(element.caption ?? '');
    const captionFocused = React.useRef(false);
    React.useEffect(() => {
        if (!captionFocused.current)
            setCaptionValue(element.caption ?? '');
    }, [element.caption]);
    const commitCaption = () => {
        captionFocused.current = false;
        const editor = mutxt.editor;
        const path = slate_react_1.ReactEditor.findPath(editor, element);
        const next = captionValue.trim();
        if (next)
            slate_1.Transforms.setNodes(editor, { caption: next }, { at: path });
        else
            slate_1.Transforms.unsetNodes(editor, 'caption', { at: path });
    };
    const captionRef = React.useRef(null);
    React.useEffect(() => {
        if (readOnly)
            return;
        return bindVoidInputKeyDown(captionRef.current, mutxt.editor, () => {
            setCaptionValue(element.caption ?? '');
            captionFocused.current = false;
        });
    }, [readOnly, element.caption, mutxt.editor]);
    const options = !readOnly && (React.createElement(Popup_1.Popup, { renderContext: () => React.createElement(FileOptionsPopup_1.FileOptionsPopup, { element: element }) },
        React.createElement(BasicButtonMore_1.BasicButtonMore, { type: "button", width: 32, height: 32, rounder: true, tooltip: t('File options'), onMouseDown: stopMouseDown })));
    const body = mediaKind ? (React.createElement(MediaPreview_1.MediaPreview, { thing: thing, kind: mediaKind })) : (React.createElement(FileListItem_1.FileListItem, { fill: true, spacious: true, icon: React.createElement(FileIcon_1.FileIcon, { label: getExtensionLabel(thing), ext: getExtensionLabel(thing), id: thing['@id'], size: 32 }), title: readOnly ? (thing.name || t('Untitled file')) : (React.createElement(flexible_input_1.FlexibleInput, { inp: setNameInputEl, value: nameValue, minWidth: 120, onChange: (e) => setNameValue(e.target.value), onFocus: () => {
                focused.current = true;
            }, onBlur: commitName })), metadata: [thing.mimeType, formatFileSize(thing.size)].filter(Boolean).join(' • '), actions: React.createElement("div", { style: { display: 'flex', gap: 16, paddingInlineEnd: 4 } },
            React.createElement(Button_1.Button, { compact: true, size: -1, icon: React.createElement(Iconista_1.Iconista, { set: 'tabler', icon: 'download', width: 16, height: 16 }), onClick: () => {
                    const state = new state_1.FileOptionsState(mutxt, element);
                    state.download();
                } }, t('Download')),
            options) }));
    return (React.createElement(Paper_1.Paper, { noOutline: true, round: true, style: {
            margin: '4px 0',
            position: 'relative',
            userSelect: 'none',
        } },
        body,
        !!mediaKind && selected && React.createElement("div", { style: { position: 'absolute', top: 0, insetInlineEnd: -46 } }, options),
        React.createElement(VoidSelectionOverlay_1.VoidSelectionOverlay, { selected: selected }),
        element.caption || !readOnly ? (React.createElement("div", { ref: captionRef, contentEditable: false, className: captionClass, style: { color: styles.g(0.42), padding: '8px 8px 4px' } }, readOnly ? (element.caption) : (React.createElement(flexible_input_1.FlexibleInput, { value: captionValue, minWidth: 80, typeahead: captionValue ? '' : t('Add caption…'), onChange: (e) => setCaptionValue(e.target.value), onFocus: () => {
                captionFocused.current = true;
            }, onBlur: commitCaption })))) : null));
};
exports.ResolvedFileCard = ResolvedFileCard;
